import { useState } from 'react'
import {
  Brain,
  Users,
  Database,
  Trophy,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Copy,
  Check,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useGlobalMemoryStats,
  useGlobalMemoryHistory,
  type GlobalHistoryParams,
} from '@/hooks/useMemory'
import { useAssistants } from '@/hooks/useAssistants'

const CATEGORIES = ['identity', 'preference', 'sro_slot', 'intent', 'service_request'] as const
const SOURCES = ['user_stated', 'llm_extracted', 'sro_completed', 'system'] as const
const SORT_OPTIONS = [
  { value: '-created_at', label: 'Newest first' },
  { value: 'created_at', label: 'Oldest first' },
  { value: 'category', label: 'Category' },
  { value: 'key', label: 'Key' },
] as const

function sourceBadge(source: string) {
  const variants: Record<string, 'default' | 'secondary' | 'outline'> = {
    user_stated: 'default',
    llm_extracted: 'secondary',
    sro_completed: 'outline',
    system: 'secondary',
  }
  return <Badge variant={variants[source] ?? 'secondary'}>{source}</Badge>
}

// ── Clipboard copy button ──────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 font-mono text-xs hover:underline cursor-pointer"
      title="Copy cp_uid"
    >
      {text.slice(0, 12)}…
      {copied ? (
        <Check className="h-3 w-3 text-green-600" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground" />
      )}
    </button>
  )
}

// ── Stats cards ────────────────────────────────────────────────────────

function StatsRow() {
  const { data, isLoading } = useGlobalMemoryStats()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Total Users</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data?.total_users ?? 0}</div>
          <p className="text-xs text-muted-foreground">Distinct cp_uid identities</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Total Facts</CardTitle>
          <Database className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data?.total_facts ?? 0}</div>
          <p className="text-xs text-muted-foreground">Memory rows (append log)</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Top Keys</CardTitle>
          <Trophy className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {!data?.top_keys?.length ? (
            <p className="text-sm text-muted-foreground">No data yet</p>
          ) : (
            <ol className="space-y-1">
              {data.top_keys.slice(0, 5).map((tk, i) => (
                <li key={`${tk.category}-${tk.key}`} className="flex items-center justify-between text-sm">
                  <span>
                    <span className="text-muted-foreground mr-1">{i + 1}.</span>
                    <span className="font-mono text-xs">{tk.key}</span>
                    <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0">{tk.category}</Badge>
                  </span>
                  <span className="text-muted-foreground">{tk.count}</span>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ── Global history table ───────────────────────────────────────────────

function GlobalHistoryTable() {
  const [params, setParams] = useState<GlobalHistoryParams>({
    ordering: '-created_at',
    page: 1,
    page_size: 25,
  })

  const { data, isLoading } = useGlobalMemoryHistory(params)
  const { data: assistantsData } = useAssistants({ page: 1, pageSize: 100 })

  const setParam = <K extends keyof GlobalHistoryParams>(key: K, value: GlobalHistoryParams[K]) => {
    setParams((prev) => ({ ...prev, [key]: value, ...(key !== 'page' ? { page: 1 } : {}) }))
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.count / (params.page_size ?? 25))) : 1

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Global History</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="Filter by cp_uid…"
            value={params.cp_uid ?? ''}
            onChange={(e) => setParam('cp_uid', e.target.value || undefined)}
            className="w-[220px] h-8 text-xs"
          />

          <Select
            value={params.assistant_id ?? 'all'}
            onValueChange={(v) => setParam('assistant_id', v === 'all' ? undefined : v)}
          >
            <SelectTrigger className="w-[180px] h-8 text-xs">
              <SelectValue placeholder="Assistant" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assistants</SelectItem>
              {assistantsData?.items
                ?.filter((a) => !!a.shared_id)
                .map((a) => (
                  <SelectItem key={a.shared_id} value={a.shared_id}>{a.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>

          <Select value={params.category ?? 'all'} onValueChange={(v) => setParam('category', v === 'all' ? undefined : v)}>
            <SelectTrigger className="w-[160px] h-8 text-xs">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={params.source ?? 'all'} onValueChange={(v) => setParam('source', v === 'all' ? undefined : v)}>
            <SelectTrigger className="w-[160px] h-8 text-xs">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={params.ordering ?? '-created_at'} onValueChange={(v) => setParam('ordering', v)}>
            <SelectTrigger className="w-[150px] h-8 text-xs">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[130px]">cp_uid</TableHead>
                <TableHead className="w-[130px]">Assistant</TableHead>
                <TableHead className="w-[110px]">Category</TableHead>
                <TableHead className="w-[120px]">Key</TableHead>
                <TableHead>Value</TableHead>
                <TableHead className="w-[110px]">Source</TableHead>
                <TableHead className="w-[130px]">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : !data || data.results.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                    No memory entries
                  </TableCell>
                </TableRow>
              ) : (
                data.results.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell><CopyButton text={r.cp_uid} /></TableCell>
                    <TableCell className="text-xs">{r.assistant_name ?? '—'}</TableCell>
                    <TableCell><Badge variant="outline">{r.category}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{r.key}</TableCell>
                    <TableCell className="break-all">{r.value}</TableCell>
                    <TableCell>{sourceBadge(r.source)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.created_at
                        ? formatDistanceToNow(new Date(r.created_at), { addSuffix: true })
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {data && data.count > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{data.count} total entries</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!data.previous}
                onClick={() => setParam('page', (params.page ?? 1) - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-muted-foreground">
                Page {params.page ?? 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!data.next}
                onClick={() => setParam('page', (params.page ?? 1) + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ── Main page ──────────────────────────────────────────────────────────

export function GlobalMemoryPage() {
  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <Brain className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Memory Browser</h1>
          <p className="text-sm text-muted-foreground">
            Tenant-wide view of all user memory across assistants
          </p>
        </div>
      </div>

      <StatsRow />
      <GlobalHistoryTable />
    </div>
  )
}
